import { useRouter } from "next/router";
import { ImageStyles } from "@business-finland/wif-ui-lib";
import {
  MasonryGrid,
  type ILinkPreview,
  type IRecommendationsCardProps,
} from "@business-finland/wif-ui-lib/dist/Masonry";

import { Viewport } from "../../../constants/app";
import useSavedItems from "../../../contexts/SaveItemsToStorageContext";
import useImageDataHandler, {
  ImageDataHandlerFn,
} from "../../../hooks/useImageDataHandler";
import { ISavedItem } from "../../../types/saved-items";
import { formatPageLink, jcrMultiFieldToArray } from "../../../util/magnolia";

interface ICard {
  id: string;
  cardTitle: string;
  cardIntroText: string;
  link: string;
  linkLabel: string;
  linkDescription: string;
  altText?: string;
  image?: Wif.Mgnl.Image;
}

interface ILinkPreviewVariant extends Wif.Mgnl.Node {
  hasSaveButton: boolean;
  card?: Wif.Mgnl.Node<{
    cardTitle: string;
    cardIntroText: string;
    link: string;
    linkLabel: string;
    linkDescription: string;
    altText?: string;
    image?: Wif.Mgnl.Image;
    field: string;
  }>;
}

interface IMasonryCardVariant extends Wif.Mgnl.Node {
  page: Wif.Mgnl.Node<Wif.Mgnl.NavPage>;
}
interface IMasonryProps {
  variants: Wif.Mgnl.NodeWithList<
    Wif.Mgnl.Node<{
      field: string;
      page?: Wif.Mgnl.Node<Wif.Mgnl.NavPage>;
      hasSaveButton?: boolean;
      card?: Wif.Mgnl.Node<
        ICard & {
          field: string;
        }
      >;
    }>
  >;
}

export function Masonry(props: IMasonryProps) {
  const { variants: rawVariants } = props;

  const { isPreview } = useRouter();
  const { isSaved, toggleItemInLocalStorage } = useSavedItems();
  const imageDataHandler = useImageDataHandler();

  const variants = jcrMultiFieldToArray(rawVariants).map((variant) => {
    if (!("page" in variant)) {
      return {
        linkPreview: mapToLinkPreviewCardProps(
          variant as ILinkPreviewVariant,
          isSaved,
          toggleItemInLocalStorage,
          imageDataHandler
        ),
      };
    }

    return {
      masonry: mapToMasonryCardProps(
        variant as IMasonryCardVariant,
        isSaved,
        toggleItemInLocalStorage,
        isPreview,
        imageDataHandler
      ),
    };
  });

  return <MasonryGrid variants={variants} />;
}

const mapToMasonryCardProps = (
  variant: IMasonryCardVariant,
  isSaved: (id: string) => boolean,
  toggleSave: (item: ISavedItem) => void,
  isPreview: boolean,
  imageDataHandler: ImageDataHandlerFn
): IRecommendationsCardProps => {
  const { page } = variant;
  const { title, description, introductionImage } = page;

  const id = page["@id"];
  const isItemSaved = isSaved(id);

  const imageData = imageDataHandler(introductionImage);
  const imageProps = {
    ...imageData,
    altText: title,
    styling: ImageStyles.MASONRY_CARD,
    credits: introductionImage?.metadata?.rights,
  };

  return {
    title,
    description,
    showSaveButton: true,
    link: formatPageLink(page["@path"], isPreview),
    image: imageProps,
    isSaved: isItemSaved,
    onClick: () => {
      toggleSave({
        id,
        description,
        image: introductionImage,
        pageLink: page["@link"] ?? "",
        title,
        type: "masonryCard",
      });
    },
  };
};

const mapToLinkPreviewCardProps = (
  linkPreviewVariant: ILinkPreviewVariant,
  isSaved: (id: string) => boolean,
  toggleSave: (item: ISavedItem) => void,
  imageDataHandler: ImageDataHandlerFn
): ILinkPreview | undefined => {
  if (!linkPreviewVariant.card) {
    return undefined;
  }

  const card = mapToCardProps(
    linkPreviewVariant.card,
    isSaved,
    toggleSave,
    imageDataHandler
  );

  return {
    card,
    hasSaveButton: linkPreviewVariant.hasSaveButton,
    hasImages: linkPreviewVariant?.card?.field === "image",
  };
};

function mapToCardProps(
  node: Wif.Mgnl.Node<ICard>,
  isSaved: (id: string) => boolean,
  toggleSave: (item: ISavedItem) => void,
  imageDataHandler: ImageDataHandlerFn
): ILinkPreview["card"] {
  const {
    image,
    cardTitle,
    cardIntroText,
    link,
    altText,
    linkLabel,
    linkDescription,
  } = node;
  const id = node["@id"];
  const isItemSaved = isSaved(id);

  if (!image || !altText) {
    return {
      cardTitle,
      cardIntroText,
      link,
      linkLabel,
      linkDescription,
      isSaved: isItemSaved,
      onSave: () => {
        toggleSave({
          id,
          linkLabel,
          linkDescription,
          pageLink: link,
          description: cardIntroText,
          title: cardTitle,
          altText,
          type: "linkPreviewCard",
        });
      },
    };
  }

  const imageData = imageDataHandler(image, { maxViewport: Viewport.sm });

  const imageProps = {
    ...imageData,
    altText,
    credits: image?.metadata?.rights,
  };
  return {
    cardTitle,
    cardIntroText,
    link,
    linkLabel,
    linkDescription,
    imageProps,
    // image,
    isSaved: isItemSaved,
    onSave: () => {
      toggleSave({
        id,
        image,
        pageLink: link,
        title: cardTitle,
        description: cardIntroText,
        type: "linkPreviewCard",
      });
    },
  };
}
